import {inject, Injectable} from '@angular/core';
import {
  DirectusService,
  DirectusTranslation,
  EvaluationSubCriteriaDirectus,
} from '@frontend/directus';
import {EvaluationCriteriaResponse} from '@frontend/shared-api';
import {firstValueFrom} from 'rxjs';
import {TabStatusEnum} from '../../../../../../../shared/tabs/tab-status.model';
import {ModuleTab, SubCriteriaForModule} from './ec3-module-group.types';
import {isEC3} from '../../../../../../../shared/constants/evaluation-criteria.constants';

interface ModuleFunction {
  id: number;
  name: string;
}

interface EvaluationSubCriteriaForModule {
  id: number;
  directusId: string;
  evaluationCriteriaId: number;
  included: boolean;
}

interface ModuleGroup {
  moduleId: number;
  moduleName: string;
  moduleTranslations: DirectusTranslation[];
  subCriteria: SubCriteriaForModule[];
}

@Injectable({
  providedIn: 'root',
})
export class Ec3ModuleGroupingService {
  private readonly directusService = inject(DirectusService);

  async loadAndGroupModuleTabs(
    evaluationCriteriaNumber: number,
    evaluationCriteriaData?: EvaluationCriteriaResponse[],
  ): Promise<ModuleTab[]> {
    const [criteriaData, subCriteriaData, moduleFunctionsMap] = await Promise.all([
      firstValueFrom(this.directusService.loadEvaluationCriteria()),
      firstValueFrom(this.directusService.loadSubCriteria()),
      firstValueFrom(this.directusService.loadModuleFunctionsGroupedByModule()),
    ]);

    const criteria3 = criteriaData.find((c) => isEC3(c.ecNumber));
    if (!criteria3) {
      return [];
    }

    const relevantSubCriteria = (subCriteriaData as EvaluationSubCriteriaDirectus[]).filter(
      (sc) => criteria3.subcriteria.includes(sc.id) && sc.modul_function !== null,
    );

    const evaluationSubCriteria = this.getEvaluationSubCriteria(
      evaluationCriteriaData,
      evaluationCriteriaNumber,
    );

    return this.createModuleTabsFromSubCriteria(
      relevantSubCriteria,
      moduleFunctionsMap,
      evaluationSubCriteria,
    );
  }

  private getEvaluationSubCriteria(
    evaluationCriteriaData?: EvaluationCriteriaResponse[],
    evaluationCriteriaNumber?: number,
  ): EvaluationSubCriteriaForModule[] {
    if (!evaluationCriteriaData || !evaluationCriteriaNumber) return [];
    const matchingCriteria = evaluationCriteriaData.find(
      (ec) => ec.ecNumber === evaluationCriteriaNumber,
    );
    return (matchingCriteria?.subCriteria || []) as EvaluationSubCriteriaForModule[];
  }

  private createModuleTabsFromSubCriteria(
    subCriteria: EvaluationSubCriteriaDirectus[],
    moduleFunctionsMap: Map<number, unknown>,
    evaluationSubCriteria: EvaluationSubCriteriaForModule[],
  ): ModuleTab[] {
    const moduleGroups = this.groupSubCriteriaByModule(
      subCriteria,
      moduleFunctionsMap,
      evaluationSubCriteria,
    );

    return this.convertModuleGroupsToTabs(moduleGroups);
  }

  private groupSubCriteriaByModule(
    subCriteria: EvaluationSubCriteriaDirectus[],
    moduleFunctionsMap: Map<number, unknown>,
    evaluationSubCriteria: EvaluationSubCriteriaForModule[],
  ): Map<number, ModuleGroup> {
    const moduleGroups = new Map<number, ModuleGroup>();

    for (const subCriterion of subCriteria) {
      const moduleInfo = this.findModuleForSubCriteria(subCriterion, moduleFunctionsMap);
      if (!moduleInfo) continue;

      this.ensureModuleGroupExists(moduleGroups, moduleInfo);
      const processedSubCriterion = this.createProcessedSubCriterion(
        subCriterion,
        evaluationSubCriteria,
      );
      this.addSubCriterionToModuleGroup(moduleGroups, moduleInfo.moduleId, processedSubCriterion);
    }

    return moduleGroups;
  }

  private ensureModuleGroupExists(
    moduleGroups: Map<number, ModuleGroup>,
    moduleInfo: {moduleId: number; moduleName: string; moduleTranslations: DirectusTranslation[]},
  ): void {
    if (!moduleGroups.has(moduleInfo.moduleId)) {
      moduleGroups.set(moduleInfo.moduleId, {
        ...moduleInfo,
        subCriteria: [],
      });
    }
  }

  private createProcessedSubCriterion(
    subCriterion: EvaluationSubCriteriaDirectus,
    evaluationSubCriteria: EvaluationSubCriteriaForModule[],
  ): SubCriteriaForModule {
    const evaluationData = evaluationSubCriteria.find((esc) => esc.directusId === subCriterion.id);

    return {
      id: evaluationData?.id || 0,
      directusId: subCriterion.id,
      scNumber: subCriterion.sc_number,
      translations: subCriterion.translations,
      evaluationCriteriaId: evaluationData?.evaluationCriteriaId || 0,
      included: evaluationData?.included || false,
      attributes: [],
      attributesLoading: false,
      loadAttempted: false,
    };
  }

  private addSubCriterionToModuleGroup(
    moduleGroups: Map<number, ModuleGroup>,
    moduleId: number,
    subCriterion: SubCriteriaForModule,
  ): void {
    const moduleGroup = moduleGroups.get(moduleId);
    if (moduleGroup) {
      moduleGroup.subCriteria.push(subCriterion);
    }
  }

  private convertModuleGroupsToTabs(moduleGroups: Map<number, ModuleGroup>): ModuleTab[] {
    return Array.from(moduleGroups.values())
      .map((group) => {
        const sortedSubCriteria = [...group.subCriteria].sort((a, b) => a.scNumber - b.scNumber);
        return {
          moduleId: group.moduleId,
          moduleName: group.moduleName,
          moduleTranslations: group.moduleTranslations,
          subCriteria: sortedSubCriteria,
          status: TabStatusEnum.NOT_STARTED,
          loaded: false,
        };
      })
      .sort((a, b) => a.moduleName.localeCompare(b.moduleName));
  }

  private findModuleForSubCriteria(
    subCriteria: EvaluationSubCriteriaDirectus,
    moduleFunctionsMap: Map<number, unknown>,
  ): {moduleId: number; moduleName: string; moduleTranslations: DirectusTranslation[]} | null {
    const functionId = subCriteria.modul_function;
    if (!functionId) return null;

    for (const [moduleId, moduleData] of moduleFunctionsMap) {
      const module = moduleData as {
        name: string;
        translations?: DirectusTranslation[];
        functions?: ModuleFunction[];
      };
      const hasFunction = module.functions?.some((f: ModuleFunction) => f.id === functionId);
      if (hasFunction) {
        return {
          moduleId,
          moduleName: module.name,
          moduleTranslations: module.translations || [],
        };
      }
    }
    return null;
  }
}
